/* Kalmflex theme behaviour. No dependencies; loaded deferred from kf-head. */
(() => {
  const t = window.KalmflexStrings || {};
  /* Custom events for Shopify customer events (Settings → Customer events → custom pixel), which forwards
     them to Meta / GA4 with the visitor's consent: kf_offer_selected, kf_add_to_cart, kf_size_guide_open. */
  const track = (name, data = {}) => window.Shopify?.analytics?.publish?.(name, data);

  /* ---------- Evergreen countdown (restarts every N hours, same for every visitor) ---------- */
  class KfCountdown extends HTMLElement {
    connectedCallback() {
      const cycle = (Number(this.dataset.cycleHours) || 3) * 3600 * 1000;
      const [h, m, s] = ['[data-h]', '[data-m]', '[data-s]'].map((q) => this.querySelector(q));
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        const left = cycle - (Date.now() % cycle);
        h.textContent = pad(Math.floor(left / 3600000));
        m.textContent = pad(Math.floor((left % 3600000) / 60000));
        s.textContent = pad(Math.floor((left % 60000) / 1000));
      };
      tick();
      this.timer = setInterval(tick, 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('kf-countdown', KfCountdown);

  /* ---------- Hero background video: 16:9 on desktop, 9:16 on phones, plays while visible, fades in over the image ---------- */
  class KfHeroVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.video.muted = true; /* required for autoplay */
      this.wide = window.matchMedia('(min-width: 961px)');
      this.calm = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = true;
      this.video.addEventListener('playing', () => this.classList.add('is-playing'));
      this.update = () => this.sync();
      this.wide.addEventListener('change', this.update);
      this.calm.addEventListener('change', this.update);
      this.observer = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; this.sync(); });
      this.observer.observe(this);
    }
    sync() {
      const src = this.wide.matches ? this.dataset.desktop : this.dataset.mobile;
      if (!src || this.calm.matches || navigator.connection?.saveData) {
        this.video.pause();
        this.classList.remove('is-playing');
        return;
      }
      if (this.video.getAttribute('src') !== src) {
        this.classList.remove('is-playing'); /* image shows while the other format loads */
        this.video.src = src;
      }
      if (this.visible) this.video.play().catch(() => {}); /* autoplay refused: the image stays */
      else this.video.pause();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.wide?.removeEventListener('change', this.update);
      this.calm?.removeEventListener('change', this.update);
    }
  }
  customElements.define('kf-hero-video', KfHeroVideo);

  /* ---------- Mechanism section: the video has no source in the markup. Sources are attached when the section
     comes within 300 px of the screen, it plays only while on screen, and the steps light up in sync with it.
     Reduced motion or data saver: nothing is downloaded, the poster stays. ---------- */
  class KfMechanism extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.media = this.querySelector('.kf-mech__media');
      this.steps = [...this.querySelectorAll('[data-start]')].map((el) => ({ el, start: parseFloat(el.dataset.start) }));
      if (!this.video || !('IntersectionObserver' in window)) return;
      this.video.muted = true; /* required for autoplay */
      this.calm = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.video.addEventListener('playing', () => { this.media.classList.add('is-playing'); this.tick(); });
      this.near = new IntersectionObserver(([e]) => { if (e.isIntersecting) { this.wanted = true; this.sync(); } }, { rootMargin: '300px 0px' });
      this.seen = new IntersectionObserver(([e]) => { this.visible = e.isIntersecting; this.sync(); }, { threshold: 0.25 });
      this.near.observe(this);
      this.seen.observe(this.media);
      this.update = () => this.sync();
      this.calm.addEventListener('change', this.update);
    }
    get allowed() { return !this.calm.matches && !navigator.connection?.saveData; }
    sync() {
      if (!this.allowed) { this.video.pause(); this.media.classList.remove('is-playing'); this.show(-1, 0); return; }
      if (this.wanted && !this.loaded) {
        this.loaded = true;
        [['webm', 'video/webm'], ['mp4', 'video/mp4']].forEach(([key, type]) => {
          if (!this.dataset[key]) return;
          const source = document.createElement('source');
          source.src = this.dataset[key];
          source.type = type;
          this.video.append(source);
        });
        this.video.load();
      }
      if (this.loaded && this.visible) this.video.play().catch(() => {}); /* autoplay refused: the poster stays */
      else if (this.loaded) this.video.pause();
    }
    tick() {
      cancelAnimationFrame(this.raf);
      const loop = () => {
        const t = this.video.currentTime;
        const d = this.video.duration || 0;
        let i = this.steps.length - 1;
        while (i > 0 && t < this.steps[i].start) i--;
        const end = i + 1 < this.steps.length ? this.steps[i + 1].start : d;
        this.show(i, end > this.steps[i].start ? (t - this.steps[i].start) / (end - this.steps[i].start) : 0);
        if (!this.video.paused) this.raf = requestAnimationFrame(loop);
      };
      loop();
    }
    show(active, progress) {
      this.steps.forEach(({ el }, i) => {
        el.classList.toggle('is-active', i === active);
        el.style.setProperty('--p', i === active ? Math.min(1, Math.max(0, progress)).toFixed(3) : 0);
      });
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
      this.near?.disconnect();
      this.seen?.disconnect();
      this.calm?.removeEventListener('change', this.update);
    }
  }
  customElements.define('kf-mechanism', KfMechanism);

  /* ---------- Buy box ---------- */
  class KfBuybox extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-kf-variants]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.setupGallery();
      this.setupOptions();
      this.setupPicker();
      this.setupOffers();
      this.setupPrices();
      this.setupAdd();
      this.setupSticky();
      this.setupVideos();
      this.querySelectorAll('[data-kf-close]').forEach((b) => b.addEventListener('click', () => b.closest('dialog').close()));
    }

    /* Gallery: thumbs, arrows, swipe, keyboard */
    setupGallery() {
      this.slides = [...this.querySelectorAll('[data-kf-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-kf-thumb]')];
      this.index = 0;
      const go = (i) => {
        this.index = (i + this.slides.length) % this.slides.length;
        this.slides.forEach((s, n) => (s.hidden = n !== this.index));
        this.thumbs.forEach((b, n) => b.setAttribute('aria-current', String(n === this.index)));
      };
      this.thumbs.forEach((b) => b.addEventListener('click', () => go(Number(b.dataset.kfThumb))));
      this.querySelector('[data-kf-prev]')?.addEventListener('click', () => go(this.index - 1));
      this.querySelector('[data-kf-next]')?.addEventListener('click', () => go(this.index + 1));
      const main = this.querySelector('.kf-gallery__main');
      let x0 = null;
      main?.addEventListener('touchstart', (e) => (x0 = e.touches[0].clientX), { passive: true });
      main?.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 40) go(this.index + (dx < 0 ? 1 : -1));
        x0 = null;
      });
    }

    /* Per-piece selects. Changing the size of a card's first piece carries over to every size the
       shopper has not set herself, in all cards, so switching offers keeps her size. */
    setupOptions() {
      this.querySelectorAll('[data-kf-unit-option]').forEach((sel) =>
        sel.addEventListener('change', () => {
          sel.dataset.touched = '1';
          const row = sel.closest('[data-kf-unit]');
          if (sel.dataset.kfUnitOption === '0' && row === row.parentElement.querySelector('[data-kf-unit]')) {
            this.querySelectorAll('[data-kf-unit-option="0"]').forEach((other) => {
              if (!other.dataset.touched) other.value = sel.value;
            });
            this.querySelectorAll('[data-kf-pick="0"]').forEach((p) => {
              const on = p.dataset.value === sel.value;
              p.classList.toggle('is-selected', on);
              p.setAttribute('aria-checked', String(on));
            });
          }
          this.refreshAvailability();
          if (this.money) this.updatePrices();
        })
      );
    }

    /* Size / side pickers above the offers. Size goes to every piece in every card; side goes to piece #1
       of each card and alternates after it (Gauche, Droite, Gauche, Droite). Cards' own selects stay
       editable afterwards. */
    setupPicker() {
      const picks = [...this.querySelectorAll('[data-kf-pick]')];
      if (!picks.length) return;
      picks.forEach((pill) =>
        pill.addEventListener('click', () => {
          const index = pill.dataset.kfPick;
          const value = pill.dataset.value;
          picks.filter((p) => p.dataset.kfPick === index).forEach((p) => {
            const on = p === pill;
            p.classList.toggle('is-selected', on);
            p.setAttribute('aria-checked', String(on));
          });
          if (index === '0') {
            this.querySelectorAll('[data-kf-unit-option="0"]').forEach((sel) => {
              sel.value = value;
              delete sel.dataset.touched;
            });
          } else {
            const values = picks.filter((p) => p.dataset.kfPick === index).map((p) => p.dataset.value);
            const other = values.find((v) => v !== value) ?? value;
            this.querySelectorAll('[data-kf-offer]').forEach((offer) =>
              offer.querySelectorAll('[data-kf-unit]').forEach((row, n) => {
                const sel = row.querySelector(`[data-kf-unit-option="${index}"]`);
                if (sel) sel.value = n % 2 === 0 ? value : other;
              })
            );
          }
          this.refreshAvailability();
          if (this.money) this.updatePrices();
        })
      );
    }

    /* Options of each piece in the selected offer, e.g. [["M", "Gauche"], ["M", "Droite"]] */
    rows(offer = this.selectedOffer()) {
      return offer ? [...offer.querySelectorAll('[data-kf-unit]')].map((row) => [...row.querySelectorAll('[data-kf-unit-option]')].map((sel) => sel.value)) : [];
    }

    findVariant(options) {
      return this.variants.find((v) => v.options.every((o, i) => o === options[i]));
    }

    refreshAvailability() {
      const btn = this.querySelector('[data-kf-add]');
      if (!btn || !this.variants.length) return;
      const rows = this.rows();
      const ok = rows.length > 0 && rows.every((opts) => this.findVariant(opts)?.available);
      btn.disabled = !ok;
      this.message(ok ? '' : t.unavailable || 'Cette combinaison est indisponible.');
    }

    /* Offer cards: click / Enter / Space selects; arrow keys move */
    setupOffers() {
      this.offers = [...this.querySelectorAll('[data-kf-offer]')];
      if (this.offers.length && !this.offers.some((o) => o.classList.contains('is-selected'))) {
        this.offers[0].classList.add('is-selected');
        this.offers[0].setAttribute('aria-checked', 'true');
      }
      const select = (offer) => {
        const changed = !offer.classList.contains('is-selected');
        this.offers.forEach((o) => {
          const on = o === offer;
          o.classList.toggle('is-selected', on);
          o.setAttribute('aria-checked', String(on));
        });
        this.refreshAvailability();
        if (this.money) this.updatePrices();
        if (changed) track('kf_offer_selected', this.offerData(offer));
      };
      this.offers.forEach((offer, i) => {
        offer.addEventListener('click', (e) => {
          if (e.target.closest('select')) return;
          select(offer);
        });
        offer.addEventListener('keydown', (e) => {
          if (e.target.closest('select, button')) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(offer); }
          if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { e.preventDefault(); const n = this.offers[(i + 1) % this.offers.length]; select(n); n.focus(); }
          if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { e.preventDefault(); const n = this.offers[(i - 1 + this.offers.length) % this.offers.length]; select(n); n.focus(); }
        });
      });
      this.refreshAvailability();
    }

    /* Offer prices in the visitor's currency. The bundle discount is stored in the shop currency and
       converted with Shopify's rate, as checkout does; variant prices are already in that currency. */
    setupPrices() {
      const cur = window.Shopify?.currency || {};
      this.rate = Number(cur.rate) || 1;
      const money = new Intl.NumberFormat(this.dataset.moneyLocale || 'fr-FR', { style: 'currency', currency: cur.active || this.dataset.currency || 'EUR' });
      this.money = (cents) => money.format(cents / 100);
      this.updatePrices();
    }

    updatePrices() {
      const v = this.findVariant(this.rows()[0] || []) || this.variants[0];
      if (!v) return;
      const compareUnit = Math.max(v.compare, v.price);
      this.offers.forEach((offer) => {
        const qty = Number(offer.dataset.qty) || 1;
        const discount = Math.round(Number(offer.dataset.discount || 0) * 100 * this.rate);
        const now = v.price * qty - discount;
        const was = compareUnit * qty;
        const saved = was - now;
        const set = (sel, text) => { const el = offer.querySelector(sel); if (el) el.textContent = text; };
        offer.dataset.kfValue = now;
        set('[data-kf-now]', this.money(now));
        set('[data-kf-was]', this.money(was));
        set('[data-kf-per-unit]', (offer.dataset.perUnit || '').replace('{price}', this.money(Math.round(now / qty))));
        set('[data-kf-saving]', (offer.dataset.saving || '')
          .replace('{amount}', this.money(saved))
          .replace('{percent}', `${Math.round((saved * 100) / was)}\u00a0%`)); // non-breaking: 17 % stays on one line
        offer.querySelectorAll('[data-kf-pending]').forEach((el) => el.removeAttribute('data-kf-pending'));
      });
      this.updateStickyPrice();
    }

    selectedOffer() {
      return this.offers.find((o) => o.classList.contains('is-selected'));
    }

    /* Analytics payload for an offer card, e.g. { offer: "1 + 1", quantity: 2, value: 59, currency: "EUR" } */
    offerData(offer) {
      return {
        offer: offer?.querySelector('.kf-offer__title')?.textContent.trim() || '',
        quantity: Number(offer?.dataset.qty) || 1,
        value: Number(offer?.dataset.kfValue || 0) / 100,
        currency: window.Shopify?.currency?.active || this.dataset.currency || 'EUR',
      };
    }

    /* Build cart lines: one per unit, merged when the same variant repeats */
    cartItems() {
      const lines = new Map();
      for (const opts of this.rows()) {
        const v = this.findVariant(opts);
        if (!v || !v.available) return { error: `${opts.join(' / ')} : ${t.unavailable || 'indisponible'}` };
        lines.set(v.id, (lines.get(v.id) || 0) + 1);
      }
      return { items: [...lines].map(([id, quantity]) => ({ id, quantity })) };
    }

    setupAdd() {
      this.addBtn = this.querySelector('[data-kf-add]');
      this.addBtn?.addEventListener('click', () => this.add());
    }

    async add(btn = this.addBtn) {
      const { items, error } = this.cartItems();
      if (error) return this.message(error);
      const label = btn.textContent;
      btn.disabled = true;
      btn.textContent = t.adding || 'Ajout…';
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: document.querySelector('kf-cart-drawer')?.dataset.sectionId }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Error');
        track('kf_add_to_cart', { ...this.offerData(this.selectedOffer()), source: btn === this.addBtn ? 'main' : 'sticky', pieces: this.rows().map((opts) => opts.join(' / ')) });
        const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
        document.querySelectorAll('[data-kf-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
        const after = this.dataset.afterAdd;
        const drawer = document.querySelector('kf-cart-drawer');
        if (after === 'drawer' && drawer) return drawer.refresh(data.sections, true);
        if (after === 'checkout') window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
        else if (after === 'cart') window.location.href = this.dataset.cartUrl;
        else this.message(t.added || 'Ajouté au panier ✓');
      } catch (e) {
        this.message(e.message);
      } finally {
        btn.disabled = false;
        btn.textContent = label;
      }
    }

    message(text) {
      const el = this.querySelector('[data-kf-msg]');
      if (el) el.textContent = text;
    }

    /* Sticky add-to-cart (mobile): visible while the main button is off-screen */
    setupSticky() {
      const bar = this.querySelector('[data-kf-sticky]');
      if (!bar || !this.addBtn) return;
      document.body.appendChild(bar);
      bar.style.position = 'fixed';
      bar.style.left = bar.style.right = '0';
      bar.style.zIndex = '30';
      const mq = window.matchMedia('(max-width: 960px)');
      new IntersectionObserver(([entry]) => {
        bar.hidden = !mq.matches || entry.isIntersecting || entry.boundingClientRect.top > 0;
      }).observe(this.addBtn);
      // Adds the selected offer (with each unit's options) straight from the bar. If that combination
      // cannot be added, scroll back to the buy box so the message and the options are in view.
      const stickyBtn = bar.querySelector('[data-kf-sticky-add]');
      stickyBtn.addEventListener('click', () => {
        if (this.addBtn.disabled || this.cartItems().error) this.addBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (!this.addBtn.disabled) this.add(stickyBtn);
      });
      this.updateStickyPrice();
    }

    updateStickyPrice() {
      const el = document.querySelector('[data-kf-sticky-price]');
      const offer = this.selectedOffer();
      if (el && offer) el.textContent = offer.querySelector('.kf-offer__now')?.textContent || '';
    }

    /* UGC tiles with a video open it in a dialog */
    setupVideos() {
      const dialog = this.querySelector('[data-kf-video-dialog]');
      const slot = dialog?.querySelector('[data-kf-video-slot]');
      this.querySelectorAll('[data-kf-video]').forEach((tile) => {
        tile.addEventListener('click', () => {
          const tpl = document.getElementById(`KfVideo-${tile.dataset.kfVideo}`);
          if (!tpl || !dialog) return;
          slot.replaceChildren(tpl.content.cloneNode(true));
          dialog.showModal();
        });
      });
      dialog?.addEventListener('close', () => slot.replaceChildren());
    }
  }
  customElements.define('kf-buybox', KfBuybox);

  /* ---------- Cart drawer: server-rendered section, re-rendered after every change ---------- */
  class KfCartDrawer extends HTMLElement {
    connectedCallback() {
      this.root = window.Shopify?.routes?.root || '/';
      this.dialog = this.querySelector('dialog');
      this.addEventListener('click', (e) => {
        if (e.target === this.dialog || e.target.closest('[data-kf-cart-close]')) return this.dialog.close();
        const line = e.target.closest('[data-kf-line]');
        if (line) return this.change(Number(line.dataset.kfLine), Number(line.dataset.quantity));
        const upsell = e.target.closest('[data-kf-upsell]');
        if (upsell) this.add([{ id: Number(upsell.dataset.kfUpsell), quantity: Number(upsell.dataset.quantity) || 1 }]);
      });
      /* Header cart icon opens the drawer; the link still works without JavaScript */
      document.addEventListener('click', (e) => {
        const link = e.target.closest('.kf-header a[href$="/cart"], [data-kf-open-cart]');
        if (!link) return;
        e.preventDefault();
        this.open();
      });
      this.tick();
    }

    open() {
      if (!this.dialog.open) this.dialog.showModal();
      this.tick();
    }

    async request(path, body) {
      this.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(`${this.root}${path}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...body, sections: this.dataset.sectionId }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || t.cartError);
        this.refresh(data.sections, true);
      } catch (e) {
        const msg = this.querySelector('[data-kf-cart-msg]');
        if (msg) msg.textContent = e.message || t.cartError;
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    add(items) { return this.request('cart/add.js', { items }); }
    change(line, quantity) { return this.request('cart/change.js', { line, quantity }); }

    /* Swap in the freshly rendered section (from a cart API response, or fetched) and open */
    async refresh(sections, open) {
      let html = sections?.[this.dataset.sectionId];
      if (!html) html = await fetch(`${this.root}?section_id=${this.dataset.sectionId}`).then((r) => r.text());
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-kf-cart-dialog]');
      if (!fresh) return;
      this.dialog.innerHTML = fresh.innerHTML;
      const count = Number(fresh.dataset.count) || 0;
      document.querySelectorAll('[data-kf-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
        const link = el.closest('a[aria-label]');
        if (link) link.setAttribute('aria-label', link.getAttribute('aria-label').replace(/\d+/, count));
      });
      if (open) this.open();
      else this.tick();
    }

    /* "Panier réservé pendant mm:ss": one reservation per browser session, restarts when it runs out */
    tick() {
      clearInterval(this.timer);
      const el = this.querySelector('[data-kf-reserve]');
      if (!el) return;
      const span = (Number(this.dataset.reserveMinutes) || 10) * 60000;
      let end = 0;
      try { end = Number(sessionStorage.getItem('kf-cart-reserve')) || 0; } catch (e) { /* storage blocked */ }
      const show = () => {
        let left = end - Date.now();
        if (left <= 0) {
          end = Date.now() + span;
          try { sessionStorage.setItem('kf-cart-reserve', String(end)); } catch (e) { /* storage blocked */ }
          left = span;
        }
        const s = Math.ceil(left / 1000);
        el.textContent = `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
      };
      show();
      this.timer = setInterval(show, 1000);
    }

    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('kf-cart-drawer', KfCartDrawer);

  /* ---------- Size guide: any [data-kf-open-size-guide] opens the drawer if present ---------- */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-kf-open-size-guide]');
    if (!trigger) return;
    const dialog = document.querySelector('[data-kf-size-guide]');
    if (!dialog) return;
    e.preventDefault();
    dialog.showModal();
    track('kf_size_guide_open', { page: location.pathname });
  });
  document.addEventListener('click', (e) => {
    if (e.target.matches('dialog.kf-drawer')) e.target.close(); /* click on backdrop */
  });
  /* Links from other pages (home « Trouver ma taille ») land on the PDP with #kf-tailles: open the guide. */
  if (location.hash === '#kf-tailles') document.querySelector('[data-kf-size-guide]')?.showModal();

  /* ---------- Mobile menu ---------- */
  document.querySelectorAll('[data-kf-burger]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const nav = document.getElementById(btn.getAttribute('aria-controls'));
      const open = !nav.classList.contains('is-open');
      nav.classList.toggle('is-open', open);
      btn.setAttribute('aria-expanded', String(open));
    });
  });

  /* ---------- Reviews: like toggle + show more ---------- */
  document.addEventListener('click', (e) => {
    const like = e.target.closest('[data-kf-like]');
    if (like) {
      const on = like.classList.toggle('is-liked');
      like.setAttribute('aria-pressed', String(on));
      const count = like.closest('.kf-review')?.querySelector('[data-kf-like-count]');
      if (count) count.textContent = Number(count.textContent) + (on ? 1 : -1);
    }
    const more = e.target.closest('[data-kf-more-reviews]');
    if (more) {
      more.closest('[data-kf-reviews]').querySelectorAll('.kf-review[hidden]').forEach((r) => (r.hidden = false));
      more.remove();
    }
  });
})();
